import { useQuery } from "@tanstack/react-query";
import { getAppSettings } from "@/lib/api";
import { publicAppSettingsKey } from "@/lib/queryClient";
import { Link } from "@tanstack/react-router";
import { useLocation } from "@tanstack/react-router";
import { hostKind } from "@/lib/hostKind";
import { usesApprovedDesign } from "@/features/navigation/VisualSystem";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";

type AuthProductShellProps = {
  testId: string;
  title: string;
  description: string;
  children: ReactNode;
};

export function AuthProductShell({
  testId,
  title,
  description,
  children,
}: AuthProductShellProps) {
  const { pathname } = useLocation();
  const approved = usesApprovedDesign(pathname, hostKind());
  const settings = useQuery({
    queryKey: publicAppSettingsKey(),
    queryFn: getAppSettings,
    staleTime: 60_000,
    enabled: approved,
  });
  return (
    <main
      data-testid={testId}
      data-auth-product-shell
      className="min-h-[calc(100svh-4rem)] bg-canvas"
    >
      <div className="grid min-h-[calc(100svh-4rem)] lg:grid-cols-[minmax(0,1fr)_minmax(24rem,0.74fr)]">
        {approved ? <ApprovedAccessPanel /> : <AccessPanel />}
        <section
          aria-label={title}
          className="flex min-h-0 flex-col justify-center px-4 py-6 sm:px-8 lg:px-14"
        >
          <div className="mx-auto flex w-full max-w-md flex-col">
            <div className="mb-7 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded bg-accent text-accent-ink">
                <TkticIntroMark />
              </span>
              <div>
                <p className="font-display text-xs uppercase tracking-wider text-ink-muted">
                  Account access
                </p>
                <p className="text-xs text-ink-muted">
                  Secure sign-in and team membership requests.
                </p>
              </div>
            </div>
            <header className="mb-6 flex flex-col gap-2">
              {approved ? (
                <p className="eyebrow auth-club-name">
                  {settings.data?.settings?.club_title || "Your club"}
                </p>
              ) : null}
              <h1 className="text-3xl font-semibold text-ink sm:text-4xl">
                {approved && title === "Sign in" ? "Welcome back." : title}
              </h1>
              <p className="text-sm leading-6 text-ink-muted">
                {approved && title === "Sign in"
                  ? "Sign in to your club workspace."
                  : description}
              </p>
            </header>
            <div className="min-h-0">{children}</div>
            {approved ? (
              <div className="auth-legal-links">
                <Link to="/terms">Beta agreement</Link>
                <span aria-hidden>·</span>
                <Link to="/privacy">Privacy notice</Link>
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </main>
  );
}

function TkticIntroMark() {
  return (
    <svg
      viewBox="0 0 96 96"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className="h-6 w-6"
    >
      <rect
        x="18"
        y="22"
        width="60"
        height="62"
        rx="6"
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
      />
      <rect x="38" y="14" width="20" height="14" rx="3" fill="currentColor" />
      <rect x="44" y="10" width="8" height="4" rx="1" fill="currentColor" />
      <circle
        cx="36"
        cy="46"
        r="7"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.5"
      />
      <g stroke="currentColor" strokeWidth="3.5" strokeLinecap="round">
        <line x1="56" y1="62" x2="66" y2="72" />
        <line x1="66" y1="62" x2="56" y2="72" />
      </g>
      <line
        x1="41"
        y1="51"
        x2="56"
        y2="63"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray="0.1 6"
        opacity="0.62"
      />
    </svg>
  );
}

function AccessPanel() {
  return (
    <aside
      aria-label="Access states"
      className="relative hidden min-h-[calc(100svh-4rem)] overflow-hidden border-r border-rule bg-paper text-navy-900 dark:border-navy-700 dark:bg-navy-900 dark:text-paper lg:block"
    >
      <AccessFieldLines />
      <div className="relative z-10 flex h-full min-h-[calc(100svh-4rem)] flex-col justify-end px-10 py-10">
        <p className="font-mono text-xs uppercase tracking-wider text-navy-900/58 dark:text-paper/65">
          / Access path
        </p>
        <h2 className="mt-3 max-w-xl text-5xl font-semibold tracking-tight text-navy-900 dark:text-paper">
          The workspace opens when access is clear.
        </h2>
        <p className="mt-4 max-w-md text-base leading-7 text-navy-900/72 dark:text-paper/72">
          Sign in with an existing account, accept an invite, or request the
          team and player link you need.
        </p>
        <div className="mt-8 grid max-w-xl grid-cols-3 gap-2">
          <StateTile label="Invite link" state="trusted path" />
          <StateTile label="Email" state="verified first" />
          <StateTile label="Team access" state="approved" />
        </div>
      </div>
    </aside>
  );
}

function AccessFieldLines() {
  return (
    <svg
      viewBox="0 0 760 900"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      className="absolute inset-0 h-full w-full"
    >
      <rect
        width="760"
        height="900"
        className="fill-paper dark:fill-navy-900"
      />
      <g className="fill-navy-900 opacity-[0.055] dark:fill-paper dark:opacity-[0.09]">
        <rect x="0" y="0" width="152" height="900" />
        <rect x="304" y="0" width="152" height="900" />
        <rect x="608" y="0" width="152" height="900" />
      </g>
      <g
        fill="none"
        strokeWidth="3"
        className="stroke-navy-900 opacity-[0.18] dark:stroke-paper dark:opacity-[0.24]"
      >
        <path d="M0 86h760M0 812h760M380 86v726" />
        <circle cx="380" cy="450" r="104" />
      </g>
    </svg>
  );
}

function StateTile({ label, state }: { label: string; state: string }) {
  return (
    <div className="rounded border border-navy-900/15 bg-navy-900/5 p-3 dark:border-paper/15 dark:bg-paper/5">
      <CheckCircle2 aria-hidden="true" className="h-4 w-4 text-accent" />
      <p className="mt-2 font-display text-sm font-semibold text-navy-900 dark:text-paper">
        {label}
      </p>
      <p className="mt-1 font-mono text-[0.65rem] uppercase tracking-wider text-navy-900/58 dark:text-paper/58">
        {state}
      </p>
    </div>
  );
}

function ApprovedAccessPanel() {
  return (
    <aside className="auth-brand-panel" aria-label="Tktic for your team">
      <p className="eyebrow">Tktic · for your team</p>
      <h2>
        Every match.
        <br />
        More to learn.
      </h2>
      <p>
        A place for the moments,
        <br />
        the feedback and the next step.
      </p>
      <div className="auth-pitch" aria-hidden="true">
        <div className="auth-pitch-line" />
        <i>8</i>
        <i>11</i>
        <i>4</i>
        <b />
      </div>
      <footer>
        <ShieldCheck size={18} />
        Your club. Your team. Your view.
      </footer>
    </aside>
  );
}
